import { Suspense, lazy } from "react";
import type { DashboardLayout, WidgetSize } from "../api/client";
import BoatHeroWidget from "./BoatHeroWidget";
import DepotWidget from "./DepotWidget";
import KontenWidget, { KontenEditControls } from "./KontenWidget";
import MonatWidget from "./MonatWidget";
import NetWorthWidget from "./NetWorthWidget";
import SparbaumWidget from "./SparbaumWidget";
import SparzieleWidget from "./SparzieleWidget";
import UmsaetzeWidget from "./UmsaetzeWidget";

export interface WidgetComponentProps {
  config: Record<string, unknown>;
  size: WidgetSize;
}

// Chart widgets pull in recharts (~400 kB) — load them on demand so the
// Übersicht's initial bundle stays lean for users who don't use them.
const BudgetPieLazy = lazy(() => import("./BudgetPieWidget"));
const HistorieLazy = lazy(() => import("./HistorieWidget"));

function ChartSkeleton() {
  return <section className="card p-5 h-[220px] animate-pulse" />;
}

function BudgetPieWidget(props: WidgetComponentProps) {
  return (
    <Suspense fallback={<ChartSkeleton />}>
      <BudgetPieLazy {...props} />
    </Suspense>
  );
}

function HistorieWidget(props: WidgetComponentProps) {
  return (
    <Suspense fallback={<ChartSkeleton />}>
      <HistorieLazy {...props} />
    </Suspense>
  );
}

/** Declarative per-widget option — rendered as a small <select> in edit
 *  mode. Values land in the widget instance's `config`. */
export interface ConfigField {
  key: string;
  label: string;
  options: { value: string | number; label: string }[];
}

/** Props for a widget's custom edit-mode header control (for config too
 *  dynamic for a static ConfigField, e.g. the Konten-Auswahl). */
export interface WidgetEditControlsProps {
  config: Record<string, unknown>;
  onConfig: (config: Record<string, unknown>) => void;
}

export interface WidgetDef {
  type: string;
  label: string;
  description: string;
  defaultSize: WidgetSize;
  defaultConfig?: Record<string, unknown>;
  configFields?: ConfigField[];
  /** Optional custom control rendered in the edit-mode frame header. */
  EditControls?: React.ComponentType<WidgetEditControlsProps>;
  Component: React.ComponentType<WidgetComponentProps>;
}

export const WIDGETS: Record<string, WidgetDef> = {
  konten: {
    type: "konten",
    label: "Kontostände",
    description:
      "Konten mit Spielraum, plus der Sparbaum-Stand — wähle, welche erscheinen.",
    defaultSize: "full",
    EditControls: KontenEditControls,
    Component: KontenWidget,
  },
  "boat-hero": {
    type: "boat-hero",
    label: "Großes Ziel",
    description: "Fortschritt deines langfristigen Ziels mit Monatsbeitrag.",
    defaultSize: "full",
    Component: BoatHeroWidget,
  },
  sparbaum: {
    type: "sparbaum",
    label: "Sparbaum",
    description: "Der komplette Zielbaum, auf- und zuklappbar.",
    defaultSize: "full",
    Component: SparbaumWidget,
  },
  monat: {
    type: "monat",
    label: "Diesen Monat",
    description: "Alltagskosten und Lifestyle: budgetiert vs. ausgegeben.",
    defaultSize: "full",
    defaultConfig: { show: "both" },
    configFields: [
      {
        key: "show",
        label: "Zeigen",
        options: [
          { value: "both", label: "Beides" },
          { value: "essential", label: "Nur Alltagskosten" },
          { value: "lifestyle", label: "Nur Lifestyle" },
        ],
      },
    ],
    Component: MonatWidget,
  },
  sparziele: {
    type: "sparziele",
    label: "Kleine Sparziele",
    description: "Vorschau deiner kleinen Sparziele mit Fortschritt.",
    defaultSize: "full",
    defaultConfig: { count: 4 },
    configFields: [
      {
        key: "count",
        label: "Anzahl",
        options: [
          { value: 2, label: "2" },
          { value: 4, label: "4" },
          { value: 6, label: "6" },
        ],
      },
    ],
    Component: SparzieleWidget,
  },
  "budget-pie": {
    type: "budget-pie",
    label: "Ausgaben-Verteilung",
    description: "Kreisdiagramm der Ausgaben im aktuellen Monat.",
    defaultSize: "half",
    Component: BudgetPieWidget,
  },
  umsaetze: {
    type: "umsaetze",
    label: "Letzte Umsätze",
    description: "Die neuesten Buchungen auf einen Blick.",
    defaultSize: "half",
    defaultConfig: { count: 5 },
    configFields: [
      {
        key: "count",
        label: "Anzahl",
        options: [
          { value: 5, label: "5" },
          { value: 8, label: "8" },
          { value: 10, label: "10" },
        ],
      },
    ],
    Component: UmsaetzeWidget,
  },
  historie: {
    type: "historie",
    label: "Ausgaben-Verlauf",
    description: "Gesamtausgaben pro Monat als Linie.",
    defaultSize: "half",
    defaultConfig: { months: 6 },
    configFields: [
      {
        key: "months",
        label: "Zeitraum",
        options: [
          { value: 3, label: "3 Monate" },
          { value: 6, label: "6 Monate" },
          { value: 12, label: "12 Monate" },
        ],
      },
    ],
    Component: HistorieWidget,
  },
  depot: {
    type: "depot",
    label: "Depots",
    description: "Gesamtwert und Positionen deiner Depots.",
    defaultSize: "half",
    Component: DepotWidget,
  },
  networth: {
    type: "networth",
    label: "Gesamtvermögen",
    description: "Kontostände plus Depot-Marktwert in einer Zeile.",
    defaultSize: "full",
    Component: NetWorthWidget,
  },
};

/** Exactly the pre-widget Übersicht — users who never customize see no
 *  change, and "Zurücksetzen" restores this. */
export const DEFAULT_LAYOUT: DashboardLayout = {
  version: 1,
  widgets: [
    { type: "konten", size: "full", config: {} },
    { type: "boat-hero", size: "full", config: {} },
    { type: "sparbaum", size: "full", config: {} },
    { type: "monat", size: "full", config: { show: "both" } },
    { type: "sparziele", size: "full", config: { count: 4 } },
  ],
};
